import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { API_URL } from '../config/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Lock, ArrowLeft, CheckCircle, AlertCircle, KeyRound, Eye, EyeOff } from 'lucide-react';

export const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [tokenValid, setTokenValid] = useState(false);
  const [userEmail, setUserEmail] = useState('');

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setError('Lien de réinitialisation invalide.');
        setVerifying(false);
        return;
      }

      try {
        const response = await axios.get(`${API_URL}/api/auth/verify-reset-token/${token}`);
        setTokenValid(true);
        setUserEmail(response.data.email);
      } catch (err) {
        setError('Ce lien de réinitialisation est invalide ou a expiré.');
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas.');
      return;
    }

    if (password.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères.');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, {
        token,
        new_password: password
      });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Réinitialiser le mot de passe | Espace Apporteur - CAMBC Conseils</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="min-h-screen pt-28 pb-20 bg-gradient-to-br from-slate-100 to-slate-200">
        <div className="max-w-md mx-auto px-4">
          <Link to="/login" className="inline-flex items-center gap-2 text-slate-600 hover:text-blue-600 transition-colors mb-6">
            <ArrowLeft size={18} />
            Retour à la connexion
          </Link>

          <Card className="shadow-2xl border-0">
            <CardHeader className="bg-gradient-to-r from-blue-700 to-blue-900 text-white rounded-t-lg text-center pb-8">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <KeyRound size={32} className="text-white" />
              </div>
              <CardTitle className="text-2xl">Nouveau mot de passe</CardTitle>
              <p className="text-blue-100 mt-2">Choisissez un mot de passe sécurisé</p>
            </CardHeader>
            <CardContent className="p-8">
              {verifying ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-700 mx-auto"></div>
                  <p className="text-slate-600 mt-4">Vérification du lien...</p>
                </div>
              ) : success ? (
                <div className="text-center py-6">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle size={32} className="text-green-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-800 mb-2">
                    Mot de passe réinitialisé !
                  </h3>
                  <p className="text-slate-600 mb-6">
                    Votre mot de passe a été modifié avec succès. Vous pouvez maintenant vous connecter.
                  </p>
                  <Link to="/login">
                    <Button className="bg-blue-700 hover:bg-blue-800">
                      Se connecter
                    </Button>
                  </Link>
                </div>
              ) : !tokenValid ? (
                <div className="text-center py-6">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <AlertCircle size={32} className="text-red-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-800 mb-2">
                    Lien invalide
                  </h3>
                  <p className="text-slate-600 mb-6">
                    {error}
                  </p>
                  <Link to="/forgot-password">
                    <Button className="bg-blue-700 hover:bg-blue-800">
                      Demander un nouveau lien
                    </Button>
                  </Link>
                </div>
              ) : (
                <>
                  {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 flex items-center gap-2">
                      <AlertCircle size={18} />
                      {error}
                    </div>
                  )}

                  <p className="text-slate-600 mb-4">
                    Réinitialisation pour : <span className="font-semibold">{userEmail}</span>
                  </p>

                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="space-y-2">
                      <Label htmlFor="password" className="text-base font-semibold">
                        Nouveau mot de passe
                      </Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                        <Input
                          id="password"
                          type={showPassword ? 'text' : 'password'}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="Minimum 8 caractères"
                          required
                          minLength={8}
                          className="h-12 pl-10 pr-10"
                          data-testid="reset-password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                          {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="confirmPassword" className="text-base font-semibold">
                        Confirmer le mot de passe
                      </Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                        <Input
                          id="confirmPassword"
                          type={showPassword ? 'text' : 'password'}
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          placeholder="Retapez le mot de passe"
                          required
                          className="h-12 pl-10"
                          data-testid="reset-confirm-password"
                        />
                      </div>
                    </div>

                    <Button
                      type="submit"
                      size="lg"
                      className="w-full bg-blue-700 hover:bg-blue-800 text-white text-lg py-6"
                      disabled={loading}
                      data-testid="reset-submit"
                    >
                      {loading ? 'Réinitialisation...' : 'Réinitialiser le mot de passe'}
                    </Button>
                  </form>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
};
